import { Opportunity, OpportunityFilters } from '../types';
import { getPreBidMeetingFilterValue } from './preBidMeeting';
import { opportunityMatchesSearch } from './opportunitySearch';

export type OpportunityFilterKey = keyof OpportunityFilters;

export interface OpportunityFilterState extends OpportunityFilters {
  searchTerm: string;
}

const filterValueFor = (opportunity: Opportunity, key: OpportunityFilterKey): string | null | undefined => {
  switch (key) {
    case 'industries':
      return opportunity.industry;
    case 'opportunityTypes':
      return opportunity.opportunity_type;
    case 'procuringTypes':
      return opportunity.procuring_type;
    case 'preBidMeeting':
      return getPreBidMeetingFilterValue(opportunity);
    case 'procuringOrganizations':
      return opportunity.procuring_organization || opportunity.city;
  }
};

export const opportunityMatchesFilters = (
  opportunity: Opportunity,
  filters: OpportunityFilterState,
  excludedFilter?: OpportunityFilterKey
): boolean => {
  if (filters.searchTerm && !opportunityMatchesSearch(opportunity, filters.searchTerm)) return false;

  const filterKeys: OpportunityFilterKey[] = [
    'industries',
    'opportunityTypes',
    'procuringTypes',
    'preBidMeeting',
    'procuringOrganizations',
  ];

  return filterKeys.every((key) => {
    if (key === excludedFilter || filters[key].length === 0) return true;
    const value = filterValueFor(opportunity, key);
    return Boolean(value && filters[key].includes(value));
  });
};

export const getFacetCounts = (
  opportunities: Opportunity[],
  filters: OpportunityFilterState,
  key: OpportunityFilterKey
): { counts: Record<string, number>; total: number } => {
  const contextualOpportunities = opportunities.filter((opportunity) =>
    opportunityMatchesFilters(opportunity, filters, key)
  );
  const counts = contextualOpportunities.reduce<Record<string, number>>((result, opportunity) => {
    const value = filterValueFor(opportunity, key);
    if (value) result[value] = (result[value] ?? 0) + 1;
    return result;
  }, {});

  return { counts, total: contextualOpportunities.length };
};

export const orderFacetOptions = (
  options: string[],
  counts: Record<string, number>,
  selectedValues: string[]
): string[] => {
  const selected = new Set(selectedValues);
  return [...new Set([...options, ...selectedValues])].sort((a, b) => {
    const aUnavailable = !selected.has(a) && (counts[a] ?? 0) === 0;
    const bUnavailable = !selected.has(b) && (counts[b] ?? 0) === 0;
    if (aUnavailable !== bUnavailable) return aUnavailable ? 1 : -1;
    return a.localeCompare(b);
  });
};

export const getDisabledFacetOptions = (
  options: string[],
  counts: Record<string, number>,
  selectedValues: string[]
): string[] => {
  const selected = new Set(selectedValues);
  return options.filter((option) => !selected.has(option) && (counts[option] ?? 0) === 0);
};
